// ABOUTME: Databricks usage metrics section for admin workspace detail page.
// ABOUTME: Displays query counts, data scanned, daily trends, and per-user breakdown.

import { useEffect, useRef, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import {
  BarChart3,
  Clock,
  Database,
  DollarSign,
  Info,
  RefreshCw,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { workspaceApi } from '@/services/api/adminClient';
import type {
  DatabricksUsageResponse,
  DatabricksDailyTrendItem,
  DatabricksUserBreakdownItem,
  DatabricksCostResponse,
  DataSource,
} from '@/types/admin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

interface DatabricksUsageSectionProps {
  workspaceId: string;
  dataSources?: DataSource[];
}

function extractWarehouseId(httpPath: string): string | null {
  const match = httpPath.match(/\/sql\/[\d.]+\/warehouses\/([^/]+)/);
  return match ? match[1] : null;
}

function formatCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDuration(ms: number): string {
  if (ms === 0) return '0s';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}min`;
}

export function DatabricksUsageSection({ workspaceId, dataSources }: DatabricksUsageSectionProps) {
  const [data, setData] = useState<DatabricksUsageResponse | null>(null);
  const [dailyTrend, setDailyTrend] = useState<DatabricksDailyTrendItem[]>([]);
  const [userBreakdown, setUserBreakdown] = useState<DatabricksUserBreakdownItem[]>([]);
  const [costData, setCostData] = useState<DatabricksCostResponse | null>(null);
  const [costLoading, setCostLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [enriching, setEnriching] = useState(false);

  // Extract warehouse ID from any data source with a Databricks http_path
  const warehouseId = dataSources
    ?.filter((ds) => ds.http_path)
    .map((ds) => extractWarehouseId(ds.http_path!))
    .find((id) => id != null) ?? null;

  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  async function loadUsageData() {
    const myId = ++requestIdRef.current;
    try {
      setLoading(true);
      const [usage, trend, users] = await Promise.all([
        workspaceApi.getDatabricksUsage(workspaceId, { days }),
        workspaceApi.getDatabricksDailyTrend(workspaceId, { days }),
        workspaceApi.getDatabricksUserBreakdown(workspaceId, { days }),
      ]);
      if (!mountedRef.current || requestIdRef.current !== myId) return;
      setData(usage as DatabricksUsageResponse);
      setDailyTrend(trend as DatabricksDailyTrendItem[]);
      setUserBreakdown(users as DatabricksUserBreakdownItem[]);
      setError(null);
    } catch (err) {
      if (!mountedRef.current || requestIdRef.current !== myId) return;
      setError(err instanceof Error ? err.message : 'Failed to load Databricks usage');
    } finally {
      if (mountedRef.current && requestIdRef.current === myId) setLoading(false);
    }
  }

  async function loadCostData() {
    // Cost data is loaded independently — the Databricks billing query is slow
    // (~10s) and should not block the usage section from rendering.
    if (!warehouseId) return;
    const myId = requestIdRef.current;
    setCostLoading(true);
    try {
      const cost = await workspaceApi.getDatabricksCost(warehouseId, { days, workspaceId });
      if (!mountedRef.current || requestIdRef.current !== myId) return;
      setCostData(cost);
    } catch {
      // Cost is optional — usage section still renders without it
    } finally {
      if (mountedRef.current && requestIdRef.current === myId) setCostLoading(false);
    }
  }

  async function loadData() {
    await loadUsageData();
    await loadCostData();
  }

  useEffect(() => {
    loadData();
  }, [workspaceId, days, warehouseId]);

  async function handleEnrich() {
    try {
      setEnriching(true);
      await workspaceApi.enrichDatabricksUsage(workspaceId);
      if (!mountedRef.current) return;
      // Only reload usage data (fast MongoDB queries), not the slow billing cost
      await loadUsageData();
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Enrichment failed');
    } finally {
      if (mountedRef.current) setEnriching(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-error-light border border-error/30 rounded-sm p-4 text-error">
        {error || 'Failed to load Databricks usage data'}
      </div>
    );
  }

  const cacheHitRate =
    data.total_queries > 0 ? ((data.cache_hits / data.total_queries) * 100).toFixed(1) : '0';
  const enrichedRate =
    data.total_queries > 0
      ? `${data.enriched_queries}/${data.total_queries}`
      : '0/0';

  return (
    <div className="space-y-6">
      {/* Header with time range and refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-surface-500" />
          <h3 className="text-sm font-medium text-surface-800">Databricks Usage</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleEnrich}
            isLoading={enriching}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Refresh Metrics
          </Button>
          {[7, 30, 90].map((d) => (
            <Button
              key={d}
              variant={days === d ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setDays(d)}
            >
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SummaryCard
          title="Total Queries"
          value={data.total_queries.toLocaleString()}
          color="indigo"
          icon={<BarChart3 className="w-4 h-4" />}
        />
        <SummaryCard
          title="Data Scanned"
          value={formatBytes(data.total_bytes_scanned)}
          color="teal"
          icon={<Database className="w-4 h-4" />}
        />
        <SummaryCard
          title="Execution Time"
          value={formatDuration(data.total_execution_time_ms)}
          color="warning"
          icon={<Clock className="w-4 h-4" />}
        />
        <SummaryCard
          title="Cache Hit Rate"
          value={`${cacheHitRate}%`}
          color="coral"
          icon={<Zap className="w-4 h-4" />}
        />
        <SummaryCard
          title="Enriched"
          value={enrichedRate}
          color="indigo"
        />
      </div>

      {/* Cost Estimation */}
      {costLoading ? (
        <Card variant="surface">
          <div className="mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-surface-300" />
            <div className="h-4 w-28 bg-surface-200 rounded-sm animate-pulse" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} variant="surface">
                <div className="h-6 w-6 bg-surface-200 rounded-sm animate-pulse mb-2" />
                <div className="h-3 w-16 bg-surface-200 rounded-sm animate-pulse mb-1" />
                <div className="h-6 w-20 bg-surface-200 rounded-sm animate-pulse" />
              </Card>
            ))}
          </div>
          <div className="h-48 bg-surface-100 rounded-sm animate-pulse" />
        </Card>
      ) : costData && costData.available ? (
        <Card variant="surface">
          <div className="mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-surface-500" />
            <h3 className="text-sm font-medium text-surface-800">Estimated Cost</h3>
            {costData.sku_name && (
              <span className="text-xs text-surface-400 bg-surface-100 px-2 py-0.5 rounded-sm">
                {costData.sku_name}
              </span>
            )}
            {costData.scope === 'workspace' && (
              <span className="text-xs text-success bg-success-light px-2 py-0.5 rounded-sm">
                This workspace
              </span>
            )}
          </div>
          {costData.error && (
            <p className="text-xs text-warning">{costData.error}</p>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <SummaryCard
              title="Estimated Cost"
              value={formatCurrency(costData.estimated_cost_usd)}
              color="warning"
              icon={<DollarSign className="w-4 h-4" />}
            />
            <SummaryCard
              title="Total DBUs"
              value={costData.total_dbus.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              color="indigo"
              icon={<BarChart3 className="w-4 h-4" />}
            />
            <SummaryCard
              title="Cost per DBU"
              value={formatCurrency(costData.cost_per_dbu)}
              color="teal"
              icon={<DollarSign className="w-4 h-4" />}
            />
            <SummaryCard
              title="SKU"
              value={costData.sku_name || 'Unknown'}
              color="coral"
            />
          </div>
          {costData.daily_costs.length > 0 && (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={costData.daily_costs}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    stroke="#6b7280"
                    tick={{ fill: '#6b7280', fontSize: 11 }}
                  />
                  <YAxis
                    stroke="#6b7280"
                    tick={{ fill: '#6b7280', fontSize: 11 }}
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                    }}
                    formatter={(value, name) =>
                      name === 'Estimated Cost'
                        ? [formatCurrency(Number(value)), name]
                        : [Number(value).toFixed(2), name]
                    }
                  />
                  <Legend />
                  <Bar
                    dataKey="estimated_cost"
                    name="Estimated Cost"
                    fill="#f59e0b"
                    radius={[2, 2, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      ) : costData && !costData.available ? (
        <div className="flex items-center gap-2 text-sm text-surface-500 bg-surface-50 border border-surface-200 rounded-sm p-3">
          <Info className="w-4 h-4 flex-shrink-0" />
          <span>Cost data requires access to system.billing.usage</span>
        </div>
      ) : null}

      {/* Daily Trend Chart */}
      <Card variant="surface">
        <div className="mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-surface-500" />
          <h3 className="text-sm font-medium text-surface-800">Daily Trend</h3>
        </div>
        {dailyTrend.length === 0 ? (
          <p className="text-sm text-surface-500">No trend data available</p>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                />
                <YAxis
                  yAxisId="left"
                  stroke="#6b7280"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#6b7280"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  tickFormatter={(v) => formatBytes(v)}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                  }}
                  labelStyle={{ color: '#1f2937' }}
                  formatter={(value, name) =>
                    name === 'Data Scanned'
                      ? [formatBytes(Number(value)), name]
                      : [Number(value), name]
                  }
                />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="queries"
                  name="Queries"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="bytes_scanned"
                  name="Data Scanned"
                  stroke="#14b8a6"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Per-User Breakdown */}
      <Card variant="surface">
        <div className="mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-surface-500" />
          <h3 className="text-sm font-medium text-surface-800">Per-User Breakdown</h3>
        </div>
        {userBreakdown.length === 0 ? (
          <p className="text-sm text-surface-500">No per-user data available</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-surface-500 border-b border-surface-200">
                  <th className="pb-3 font-medium">User</th>
                  <th className="pb-3 font-medium text-right">Queries</th>
                  <th className="pb-3 font-medium text-right">Data Scanned</th>
                  <th className="pb-3 font-medium text-right">Execution Time</th>
                </tr>
              </thead>
              <tbody>
                {userBreakdown.map((user) => (
                  <tr key={user.user_id} className="border-b border-surface-100">
                    <td className="py-3">
                      <p className="text-surface-800 font-medium text-sm">
                        {user.name || user.email || 'Unknown'}
                      </p>
                      {user.email && user.email !== user.name && (
                        <p className="text-surface-500 text-xs">{user.email}</p>
                      )}
                    </td>
                    <td className="py-3 text-right text-surface-800">
                      {user.queries.toLocaleString()}
                    </td>
                    <td className="py-3 text-right text-surface-800">
                      {formatBytes(user.bytes_scanned)}
                    </td>
                    <td className="py-3 text-right text-surface-800">
                      {formatDuration(user.execution_time_ms)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

interface SummaryCardProps {
  title: string;
  value: string;
  color: 'indigo' | 'teal' | 'warning' | 'coral';
  icon?: React.ReactNode;
}

function SummaryCard({ title, value, color, icon }: SummaryCardProps) {
  const colorClasses = {
    indigo: 'bg-primary-50 text-primary-600',
    teal: 'bg-success-light text-success',
    warning: 'bg-warning-light text-warning',
    coral: 'bg-error-light text-error',
  };

  return (
    <Card variant="surface">
      <div className={`inline-block p-1.5 rounded-sm mb-2 ${colorClasses[color]}`}>
        {icon || <Database className="w-4 h-4" />}
      </div>
      <p className="text-xs text-surface-500">{title}</p>
      <p className="text-lg font-semibold text-surface-800">{value}</p>
    </Card>
  );
}

export default DatabricksUsageSection;
